/**
 * Polling Simulator tab — debugging tool for "what would the polling
 * layer return for this set of contexts?".
 *
 * Lets Paul (or any admin) input a set of canonical context tags and
 * see ranked articles, which contexts overlapped on each, the SQL ranking
 * formula, and a transparency note.
 *
 * Useful for:
 *   - Validating that polling_service queries return what intelligence
 *     engines expect (regression-check after retag work)
 *   - Discovering coverage gaps ("nothing comes back for X user profile")
 *   - Confirming canonical tag correctness (typos in production show up
 *     as zero matches)
 */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Code2, Play, Search } from 'lucide-react';
import { adminFetch } from '../../../utils/adminApi';
import type { PollingSimulatorRequest, PollingSimulatorResponse } from './types';

function TagListInput({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-600">{label}</label>
      <input
        type="text"
        value={value.join(', ')}
        onChange={(e) =>
          onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))
        }
        placeholder={placeholder}
        className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm font-mono"
      />
      <div className="mt-1 text-[10px] text-gray-400">comma-separated canonical tags</div>
    </div>
  );
}

export function PollingSimulatorTab() {
  const [conditions, setConditions] = useState<string[]>([]);
  const [demographics, setDemographics] = useState<string[]>([]);
  const [sexHormone, setSexHormone] = useState<string[]>([]);
  const [goals, setGoals] = useState<string[]>([]);
  const [sports, setSports] = useState<string[]>([]);
  const [knowledgeType, setKnowledgeType] = useState<'research' | 'practitioner' | 'all'>('research');
  const [limit, setLimit] = useState(20);
  const [showSql, setShowSql] = useState(false);

  const simulate = useMutation({
    mutationFn: (payload: PollingSimulatorRequest) =>
      adminFetch<PollingSimulatorResponse>(
        '/api/admin/researcher-knowledge/polling-simulator',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      ),
  });

  const run = () => {
    if (
      conditions.length === 0 &&
      demographics.length === 0 &&
      sexHormone.length === 0 &&
      goals.length === 0 &&
      sports.length === 0
    ) {
      return;
    }
    simulate.mutate({
      condition_contexts: conditions,
      demographic_contexts: demographics,
      sex_hormone_contexts: sexHormone,
      goal_contexts: goals,
      sport_contexts: sports,
      knowledge_type: knowledgeType,
      limit,
    });
  };

  const result = simulate.data;
  const noInput =
    conditions.length === 0 && demographics.length === 0 && sexHormone.length === 0 &&
    goals.length === 0 && sports.length === 0;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {/* Input panel */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm lg:col-span-1">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Input Contexts
        </h3>
        <div className="space-y-3">
          <TagListInput
            label="Conditions"
            placeholder="e.g. lower_back_pain_chronic, knee_patellofemoral_pain"
            value={conditions}
            onChange={setConditions}
          />
          <TagListInput
            label="Demographics"
            placeholder="e.g. age_young_adult_18_34, masters_athlete_35_54"
            value={demographics}
            onChange={setDemographics}
          />
          <TagListInput
            label="Sex / Hormone"
            placeholder="e.g. sex_female, perimenopause"
            value={sexHormone}
            onChange={setSexHormone}
          />
          <TagListInput
            label="Goals"
            placeholder="e.g. weight_loss, marathon_completion"
            value={goals}
            onChange={setGoals}
          />
          <TagListInput
            label="Sports"
            placeholder="e.g. running, cycling, strength_training"
            value={sports}
            onChange={setSports}
          />

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-gray-600">Knowledge type</label>
              <select
                value={knowledgeType}
                onChange={(e) => setKnowledgeType(e.target.value as typeof knowledgeType)}
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              >
                <option value="research">Academic only</option>
                <option value="practitioner">Practitioner only</option>
                <option value="all">All</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Result limit</label>
              <input
                type="number"
                min={1}
                max={100}
                value={limit}
                onChange={(e) => setLimit(Math.max(1, Math.min(100, +e.target.value || 20)))}
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
          </div>

          <button
            onClick={run}
            disabled={noInput || simulate.isPending}
            className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Play className="h-4 w-4" /> {simulate.isPending ? 'Running…' : 'Run Simulator'}
          </button>
          {noInput && (
            <div className="text-xs text-gray-500">
              Enter at least one tag in any dimension to run.
            </div>
          )}
        </div>
      </div>

      {/* Results panel */}
      <div className="lg:col-span-2 space-y-4">
        {!result && !simulate.isPending && (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
            <Search className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">
              Enter context tags on the left and click Run Simulator to see what
              the polling layer would return.
            </p>
          </div>
        )}

        {simulate.isError && (
          <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
            Simulator failed. Check console for details.
          </div>
        )}

        {result && (
          <>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 text-sm">
              <div>
                <span className="font-medium text-gray-900">{result.result_count}</span>{' '}
                <span className="text-gray-500">articles matched</span>
              </div>
              <button
                onClick={() => setShowSql(!showSql)}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              >
                <Code2 className="h-3 w-3" /> {showSql ? 'Hide' : 'Show'} ranking formula
              </button>
            </div>

            {showSql && (
              <div className="rounded border border-gray-200 bg-gray-900 p-3">
                <div className="text-xs text-gray-400">Ranking formula:</div>
                <code className="mt-1 block whitespace-pre-wrap text-xs text-emerald-300">
                  {result.ranking_formula}
                </code>
                <div className="mt-2 text-[10px] text-gray-500">{result.note}</div>
              </div>
            )}

            <div className="space-y-2">
              {result.results.map((r, idx) => (
                <div key={r.id} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-400">#{idx + 1}</span>
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-700">
                          {r.knowledge_type}
                        </span>
                        {r.quality_score != null && (
                          <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-800">
                            Q: {r.quality_score}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 font-medium text-gray-900 line-clamp-2">{r.title}</div>
                      <div className="text-xs text-gray-500">
                        {(r.authors || []).slice(0, 3).join(', ')}{(r.authors?.length || 0) > 3 ? ', …' : ''}
                        {r.journal && ` · ${r.journal}`}
                        {r.publication_year && ` · ${r.publication_year}`}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs">
                    {r.matched_conditions.length > 0 && (
                      <div>
                        <span className="font-medium text-emerald-700">Conditions:</span>{' '}
                        <span className="text-gray-700">{r.matched_conditions.join(', ')}</span>
                      </div>
                    )}
                    {r.matched_demographics.length > 0 && (
                      <div>
                        <span className="font-medium text-blue-700">Demographics:</span>{' '}
                        <span className="text-gray-700">{r.matched_demographics.join(', ')}</span>
                      </div>
                    )}
                    {r.matched_sex_hormone.length > 0 && (
                      <div>
                        <span className="font-medium text-purple-700">Sex/Hormone:</span>{' '}
                        <span className="text-gray-700">{r.matched_sex_hormone.join(', ')}</span>
                      </div>
                    )}
                    {r.matched_goals.length > 0 && (
                      <div>
                        <span className="font-medium text-amber-700">Goals:</span>{' '}
                        <span className="text-gray-700">{r.matched_goals.join(', ')}</span>
                      </div>
                    )}
                    {r.matched_sports.length > 0 && (
                      <div>
                        <span className="font-medium text-cyan-700">Sports:</span>{' '}
                        <span className="text-gray-700">{r.matched_sports.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {result.result_count === 0 && (
                <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  No articles matched these contexts. Either the tags are non-canonical
                  or there's a coverage gap. Try the Library tab to confirm tag spelling.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
